import {DEV_USER_DUMMY, environment} from "../env";
import {makeAutoObservable} from "mobx";
import type {MithraLoginResponse} from "../services/ApiTypes";
import type {AxiosError, AxiosInstance, InternalAxiosRequestConfig} from "axios";
import {isAxiosError} from "axios";
import type AuthApi from "../services/AuthApi";
import type {User} from "../services/classes/User";
import {routes} from "../routing/routes";
import {analyticsLoginEvent, analyticsLogoutEvent} from "../services/analytics/AnalyticEvents";
import type {Location, NavigateFunction, NavigateOptions} from "react-router";
import type {AlertMsg, AuthProvider, MithraAuthToken} from "./AuthTypes";
import {isAuthenticationProvider, isTokenExpired, SESSION_STORAGE_PREFIX} from "./AuthTypes";
import type {MithraAuthContext, NavigationIntent} from "./AuthContext";
import {onFronteggLogout} from "./frontegg/FrontEggLogout";
import {onAuth0Logout} from "./auth0/Auth0Logout";
import {isLoginRouteState, LOGIN_STATE_SESSION_KEY} from "./routing/LoginRouteState";
import {locationToURL} from "../routing/routing.utils";
import {clearSavedSpendLandscapeFilters} from "../stores/managers/spend-landscape/spendLandscapeFilterStorage";

export class AuthManager {

    authenticatedUser: User | undefined

    /**
     * Total login process is in progress
     */
    isLoggingIn: AuthProvider | false = false

    /**
     * Provider login succeeded, and now waiting for Mithra login to complete
     */
    isMithraLoggingIn = false

    /**
     * Token is expired, not implemented yet
     */
    isExpired = false

    alert: AlertMsg | null = null;

    // user: Partial<User> | undefined
    mithraAuthToken: MithraAuthToken | undefined

    constructor(
        public readonly api: AuthApi,
    ) {
        if (environment.devLogin) {
            this.authenticatedUser = DEV_USER_DUMMY
            this.mithraAuthToken = {access_token: '', refresh_token: '', provider: 'dummy' as AuthProvider}
        } else {
            // Retrieve session storage
            const prevAccesToken = sessionStorage.getItem(SESSION_STORAGE_PREFIX + 'access_token');
            const prevRefreshToken = sessionStorage.getItem(SESSION_STORAGE_PREFIX + 'refresh_token');
            let prevProvider = sessionStorage.getItem(SESSION_STORAGE_PREFIX + 'provider');

            if (prevAccesToken && prevRefreshToken && !prevProvider) {
                // Migrate provider
                prevProvider = 'auth0'
                sessionStorage.setItem(SESSION_STORAGE_PREFIX + 'provider', prevProvider);
            }

            if (prevAccesToken && prevRefreshToken && isAuthenticationProvider(prevProvider)) {
                this.mithraAuthToken = {
                    access_token: prevAccesToken,
                    refresh_token: prevRefreshToken,
                    provider: prevProvider,
                };
            }
        }
        makeAutoObservable(this);
    }

    setAuthenticatedUser(user: User) {
        this.authenticatedUser = user;
        sessionStorage.setItem(SESSION_STORAGE_PREFIX + 'user', JSON.stringify(this.authenticatedUser));
        analyticsLoginEvent({
            mithra_id: this.authenticatedUser.email || this.authenticatedUser.username || String(this.authenticatedUser.id),
            email_domain: this.authenticatedUser.email.split('@')[1] || '',
            is_mithra_user: '@mithra-ai.com' === this.authenticatedUser.email.slice(-14) ? 'true' : 'false',
            is_dennis_user: 'dennis@mithra-ai.com' === this.authenticatedUser.email ? 'true' : 'false',
        })
    }

    onChangeUserData(user: User) {
        if (!this.authenticatedUser || this.authenticatedUser.id !== user.id)
            throw new Error('The user was not authenticated');
        this.authenticatedUser = user;
        sessionStorage.setItem(SESSION_STORAGE_PREFIX + 'user', JSON.stringify(this.authenticatedUser));
    }

    setToken(serverResp: MithraLoginResponse, provider: AuthProvider) {
        this.mithraAuthToken = {
            access_token: serverResp.access,
            refresh_token: serverResp.refresh,
            provider,
        };
        sessionStorage.setItem(SESSION_STORAGE_PREFIX + 'access_token', this.mithraAuthToken.access_token);
        sessionStorage.setItem(SESSION_STORAGE_PREFIX + 'refresh_token', this.mithraAuthToken.refresh_token);
        sessionStorage.setItem(SESSION_STORAGE_PREFIX + 'provider', this.mithraAuthToken.provider);
    }

    /** Updates access (and optional rotated refresh) after a successful JWT refresh call. */
    applyTokenRefresh(access: string, refresh?: string) {
        if (!this.mithraAuthToken) {
            return;
        }
        this.mithraAuthToken = {
            ...this.mithraAuthToken,
            access_token: access,
            ...(refresh !== undefined ? {refresh_token: refresh} : {}),
        };
        sessionStorage.setItem(SESSION_STORAGE_PREFIX + 'access_token', access);
        if (refresh !== undefined) {
            sessionStorage.setItem(SESSION_STORAGE_PREFIX + 'refresh_token', refresh);
        }
    }

    clearSession() {
        sessionStorage.clear();
        // Remembered filters hold ids from the customer's data: they end with the session, like the token
        clearSavedSpendLandscapeFilters();
        this.authenticatedUser = undefined;
        this.mithraAuthToken = undefined;
    }

    logout(authContext: MithraAuthContext, navigate: NavigateFunction) {
        const provider = this.mithraAuthToken?.provider;
        const redirectToLogin: NavigationIntent = {navigate, route: routes.login};

        analyticsLogoutEvent();
        this.clearSession();

        // TODO: Call signout for Firebase User: https://firebase.google.com/docs/auth/web/password-auth#next_steps

        if (provider === 'auth0') {
            if (authContext.auth0 === null) throw new Error('Auth0 context must be provided!');
            onAuth0Logout(authContext.auth0, redirectToLogin);
        } else if (provider === 'front_egg') {
            if (authContext.frontEgg === null) throw new Error('FrontEgg context must be provided!');
            onFronteggLogout(authContext.frontEgg, redirectToLogin);
        } else {
            console.error('AuthManager: Unknown provider', provider);
        }
    }

    /** Set when any API returns 401 with code token_not_valid. */
    markTokenInvalid() {
        this.isExpired = true;
    }

    resetExpiredFlag() {
        this.isExpired = false;
    }

    catchApiError(err: AxiosError<any>) {
        if (isTokenExpired(err)) {
            this.markTokenInvalid();
            this.clearSession();
            return false;
        }
        return true;
    }

    processMithraLoginSuccess(resp: MithraLoginResponse, location: Location<any>, navigate: NavigateFunction, provider: AuthProvider, rollbar?: any) {
        if(resp.access === undefined && (resp as any).access_token) {
            (rollbar ?? console).error('AuthManager: Mithra login interface mismatch', Object.keys(resp));
            this.processMithraLoginError('Login failed; not up-to-date');
            return
        }

        console.log(`AuthManager: Mithra authentication successful of ${provider}`, location.state);
        this.setToken(resp, provider);
        this.setAuthenticatedUser(resp.user);
        this.alert = null;
        this.isExpired = false;
        this.isLoggingIn = false;
        this.isMithraLoggingIn = false;

        let redirect: string = locationToURL(location);
        let options: NavigateOptions | undefined = undefined;
        const state = sessionStorage.getItem(LOGIN_STATE_SESSION_KEY);

        if (isLoginRouteState(location.state)) {
            redirect = location.state.loginIntention.onSuccessRedirectTo;
            options = location.state.loginIntention.onSuccessRedirectOptions;
        } else {
            if (isLoginRouteState(state)) {
                redirect = state.loginIntention.onSuccessRedirectTo;
                options = state.loginIntention.onSuccessRedirectOptions;
            }
            if (state) {
                sessionStorage.removeItem(LOGIN_STATE_SESSION_KEY);
            }
        }
        if (redirect === routes.login) {
            // Prevent redirect loop
            redirect = routes.home;
        }

        navigate(redirect, options);
    }

    processMithraLoginError(reason: AxiosError | Error | string) {
        let alertMsg: string = '';
        if (!reason) {
            // Unknown error
            alertMsg = 'Login failed, please try again';
        } else if (typeof reason === 'string') {
            // Error message
            alertMsg = reason;
        } else if (isAxiosError(reason) && reason.response) {
            // HTTP error
            if (reason.response.data) {
                if (reason.response.data.detail) {
                    alertMsg = `${reason.response.data.detail}`;
                }
                if (!alertMsg && reason.response.data.non_field_errors) {
                    alertMsg = `${reason.response.data.non_field_errors}`;
                }
            }
            if (!alertMsg) {
                alertMsg = reason.response.statusText ? `${reason.response.statusText}` : 'Login failed';
            }
        } else {
            // Other error
            alertMsg = reason.message || String(reason) || 'Login failed';
            alertMsg = `${alertMsg}, please try again`
        }
        this.alert = {msg: alertMsg, severity: 'error'}

        this.isLoggingIn = false;
        this.isMithraLoggingIn = false;
    }

    public wrapAuthorized(...axios: AxiosInstance[]) {
        const interceptor: (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig = config => {
            if (this.mithraAuthToken?.access_token) {
                config.headers['Authorization'] = `Bearer ${this.mithraAuthToken.access_token}`;
            }
            return config;
        };
        for (const http of axios) {
            http.interceptors.request.use(interceptor);
        }
    }

    public wrapUnauthorizedResponse(...axios: AxiosInstance[]) {
        for (const http of axios) {
            http.interceptors.response.use(
                res => res,
                (error: AxiosError) => {
                    // if (!environment.devLogin && isAxiosError(error) && isTokenExpired(error)) {
                    if (isAxiosError(error) && isTokenExpired(error)) {
                        this.markTokenInvalid();
                    }
                    return Promise.reject(error);
                },
            );
        }
    }

    startLogin(provider: AuthProvider) {
        this.authenticatedUser = undefined;
        this.isLoggingIn = provider;
        this.isMithraLoggingIn = false;
    }

    setAlert(alert: AlertMsg | null) {
        this.alert = alert;
    }

    setIsMithraLoggingIn(provider: AuthProvider) {
        this.isLoggingIn = provider;
        this.isMithraLoggingIn = true;
    }
}

